import { IonChip, IonIcon, IonItem, IonLabel, IonNote, IonSkeletonText } from "@ionic/react";
import Timestamp from "./Timestamp";
import OCGatewayClient from "../client/OCGatewayClient";
import styles from './DeviceEventItem.module.css';
import { useImage } from 'react-image';
import { star } from "ionicons/icons";
import EventSummaryDescription from "./EventSummaryDescription";
import { useDeviceEventQuery } from "../query/deviceEventQuery";
import { useGetUserStarredEventQuery } from "../query/getUserStarredEventQuery";
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { eventModalHref } from '../utils/eventModalLocation';
import { useEventSummaryQuery } from "../query/eventSummaryQuery";
import { EventSummary } from "../models/EventSummary";
import { EventClassification } from "../models/DeviceEvent";
import TransitDirectionIcon from "./TransitDirectionIcon";
import { colorForEventClassification, iconForEventClassification, labelForEventClassification } from "../utils/eventClassificationUtils";

const POSTER_FRAME_PLACEHOLDER_SRC = "/assets/event/placeholder.jpg";

interface DeviceEventItemProps {
    deviceId: string;
    eventId: number;
    accessToken?: string | null;
}

function getOutcomeDirection(eventSummary?: EventSummary | null): 'inward' | 'outward' | 'peek-inward' | 'peek-outward' | 'deny-inward' | 'deny-outward' | undefined {
    if (!eventSummary || eventSummary.subevents.length === 0) {
        return undefined;
    }

    const lastSubevent = eventSummary.subevents[eventSummary.subevents.length - 1];
    const precedingSubevents = eventSummary.subevents.slice(0, -1);

    if (lastSubevent.action === 'PEEK') {
        const isPeekOnlyEvent = precedingSubevents.every((subevent) => subevent.action === 'PEEK');

        if (isPeekOnlyEvent) {
            return lastSubevent.direction === 'INWARD' ? 'peek-inward' : 'peek-outward';
        }
    }

    if (lastSubevent.action !== 'TRANSIT') {
        if (lastSubevent.action === 'DENY') {
            return lastSubevent.direction === 'INWARD' ? 'deny-inward' : 'deny-outward';
        }

        return undefined;
    }

    const isSingleTransit = eventSummary.subevents.length === 1;
    const isPeekThenTransit = precedingSubevents.every((subevent) => subevent.action === 'PEEK');

    if (!isSingleTransit && !isPeekThenTransit) {
        return undefined;
    }

    if (lastSubevent.direction === 'INWARD') {
        return 'inward';
    }

    if (lastSubevent.direction === 'OUTWARD') {
        return 'outward';
    }

    return undefined;
}

const DeviceEventItem: React.FC<DeviceEventItemProps> = ({ deviceId, eventId, accessToken }) => {
    const { t, i18n } = useTranslation('components');
    const activeLocale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
    const query = useDeviceEventQuery(deviceId, eventId, accessToken);
    const eventSummaryQuery = useEventSummaryQuery(deviceId, eventId, accessToken);
    const starredQuery = useGetUserStarredEventQuery(deviceId, eventId);
    const outcomeDirection = getOutcomeDirection(eventSummaryQuery.data);
    const isEventSummaryLoading = eventSummaryQuery.isLoading || eventSummaryQuery.data == null;

    const history = useHistory();
    const eventHref = `/events/${deviceId}/${eventId}${accessToken ? `?t=${accessToken}` : ''}`;

    // real anchor with the canonical shareable path, but in-app it swaps the
    // modal's search param onto the current location (replace, not push) so
    // the page underneath stays mounted and no history entry stacks
    const openEvent = (clickEvent: React.MouseEvent) => {
        if (clickEvent.metaKey || clickEvent.ctrlKey || clickEvent.shiftKey) {
            return;
        }

        clickEvent.preventDefault();
        history.replace(eventModalHref(history.location, deviceId, eventId, accessToken));
    };

    // The event's own token, so the poster frame stays viewable once the gateway
    // requires a credential on frame requests. Falls back to the token this item
    // was rendered with (shared event lists), and is simply absent for events
    // that predate access tokens.
    const posterFrameToken = query.data?.accessToken ?? accessToken;

    const posterFrameImage = useImage({
        srcList: query.data
            ? [
                `${OCGatewayClient.apiURL}/events/${deviceId}/${eventId}/${query.data.posterFrameIndex || 0}`
                + (posterFrameToken ? `?t=${encodeURIComponent(posterFrameToken)}` : '')
            ]
            : [],
        useSuspense: false
    });

    if (query.isLoading) {
        return <IonItem href={eventHref} onClick={openEvent} className={styles.item}>
            <div slot="start" className={styles.imageContainer}>
                <IonSkeletonText className={styles.image} animated={true}></IonSkeletonText>
            </div>
            <IonLabel>
                <div className={styles.eventOutcome}>
                    <IonSkeletonText animated={true} style={{ width: '180px' }} />
                </div>
                <IonNote><IonSkeletonText animated={true} style={{ width: '80px' }} /></IonNote>
            </IonLabel>
        </IonItem>
    }
    else if (query.isError) {
        return <IonItem href={eventHref} onClick={openEvent} className={styles.item}>
            <IonLabel>
                <div className={styles.chips}>
                    <IonChip color="medium">
                        <IonLabel>{t('device_event_item.error')}</IonLabel>
                    </IonChip>
                </div>
                <IonNote>{t('device_event_item.failed_to_load_event')}</IonNote>
            </IonLabel>
        </IonItem>
    }
    else {
        const event = query.data!;
        const posterFrameSrc = posterFrameImage.error
            ? POSTER_FRAME_PLACEHOLDER_SRC
            : posterFrameImage.src;
        const showEventClassification = (
            event.eventClassification === EventClassification.Suspicious ||
            event.eventClassification === EventClassification.Contraband ||
            event.eventClassification === EventClassification.HumanActivity ||
            event.eventClassification === EventClassification.RemoteUnlock
        );

        return <IonItem href={eventHref} onClick={openEvent} className={styles.item}
            detail={starredQuery.data?.isStarred ? false : true}
            >
            <div slot="start" className={styles.imageContainer}>
                {!posterFrameImage.isLoading
                    ? <img className={styles.image} src={posterFrameSrc} alt="" />
                    : <IonSkeletonText className={styles.image} animated={true}></IonSkeletonText>}
                {outcomeDirection && <TransitDirectionIcon direction={outcomeDirection} className={styles.outcomeIcon} />}
            </div>
            <IonLabel>
                <div className={styles.textContent}>
                    {showEventClassification && (
                        <div className={`${styles.classificationWarning} ion-color-${colorForEventClassification(event.eventClassification)}`}>
                            <IonIcon icon={iconForEventClassification(event.eventClassification)} />
                            <IonLabel>{labelForEventClassification(event.eventClassification, t)}</IonLabel>
                        </div>
                    )}
                    <EventSummaryDescription
                        className={styles.eventOutcome}
                        deviceId={deviceId}
                        eventSummary={eventSummaryQuery.data}
                        isLoading={isEventSummaryLoading}
                        locale={activeLocale}
                        skeletonStyle={{ width: '180px' }}
                        unknownLabel={t('device_event_item.unknown')}
                    />
                    <IonNote><Timestamp timestamp={event.timestamp || undefined} /></IonNote>
                </div>
            </IonLabel>
            {starredQuery.data?.isStarred && (
                <IonIcon slot="end" icon={star} className={styles.starIcon}></IonIcon>
            )}
        </IonItem>
    }
};

export default DeviceEventItem;
